import React from 'react';
import { useNavigate } from 'react-router-dom';
import { InteractiveContentCardV2 } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { useGetCustomerCredentialQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { getAppRoute } from 'src/utils/routing';
import { ROUTES } from 'src/constants/route';
import { BUNDLE_NAME, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

export interface CredentialCourseCardProps {
  /** Credential learning pathway id the card links to (pilot: amazon-pricing-credential). */
  credentialId: string;
}

/**
 * Credential ingress card prepended to the "Take a self-guided course" section as the
 * carousel's leading card. Sources its title and description from the credential
 * (GetCustomerCredential), reflects the seller's progress in the status pill, and navigates
 * to the credential page. The homepage gates rendering on the credentials pilot weblab.
 */
const CredentialCourseCard: React.FC<CredentialCourseCardProps> = ({ credentialId }) => {
  const navigate = useNavigate();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);
  const { clickMetric } = useUserMetrics();

  const { data, isLoading, isError } = useGetCustomerCredentialQuery({
    credentialId,
    clientId,
    selLocale,
  });

  if (isLoading || isError || !data) {
    return null;
  }

  const learningPathway = data.customerCredential?.credential?.learningPathway;
  if (!learningPathway) {
    return null;
  }

  const { customerCredential } = data;
  const buildStatusPill = () => {
    if (customerCredential.award) {
      return { status: 'complete' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_COMPLETED) };
    }
    if (customerCredential.assessment?.state === 'IN_PROGRESS') {
      return { status: 'in-progress' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_IN_PROGRESS) };
    }
    return { status: 'todo' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_NOT_STARTED) };
  };

  return (
    <InteractiveContentCardV2
      layout="vertical"
      imageSrc="https://d1dspppqvg0rnv.cloudfront.net/assets/de46b560-5fac-4664-bd1d-53fd190a406e/original.png"
      contentVariant="Pdf"
      title={t(credentialBundle, learningPathway.title)}
      previewDescription={t(credentialBundle, learningPathway.description)}
      statusPill={buildStatusPill()}
      ribbon={t(credentialBundle, CREDENTIAL_STRING_ID.HOME_COURSE_CERTIFICATE_RIBBON)}
      onClick={() => {
        clickMetric({
          feature: FeatureName.CredentialCardIngress,
          metadata: { url: window.location.href, credentialId },
        });
        navigate(getAppRoute(`${ROUTES.NEW_LEARN.CREDENTIAL}/${credentialId}`));
      }}
      className="h-full"
    />
  );
};

export default CredentialCourseCard;
